import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Homeowner",
      location: "Downtown District",
      rating: 5,
      text: `Clean360 transformed our home completely! Their deep cleaning service was thorough and professional. The team arrived on time, used eco-friendly products, and left our house spotless. I'm amazed by the attention to detail.`,
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=300",
      service: "Deep Cleaning"
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      role: "Business Owner",
      location: "Business District",
      rating: 5,
      text: `Outstanding commercial cleaning service! Clean360 has been maintaining our office for over a year. Their consistency and reliability are unmatched. Our employees love coming to a clean, fresh workspace every morning.`,
      image: "https://images.pixabay.com/photo/2507/man-person-suit-united-states.jpg?auto=compress&cs=tinysrgb&w=300",
      service: "Office Cleaning"
    },
    {
      id: 3,
      name: "Emily Chen",
      role: "Property Manager",
      location: "Residential Complex",
      rating: 5,
      text: `The carpet cleaning service exceeded all expectations. They removed stains I thought were permanent and the carpets look brand new. Professional, efficient, and reasonably priced. Highly recommend!`,
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b5e5?auto=format&fit=crop&w=300&q=80",
      service: "Carpet Cleaning"
    },
    {
      id: 4,
      name: "David Thompson",
      role: "Homeowner",
      location: "Suburban Area",
      rating: 5,
      text: `Clean360 installed beautiful vinyl flooring in our kitchen and bathrooms. The quality of work is exceptional and the team was professional throughout. Our home value has definitely increased!`,
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=300",
      service: "Vinyl Flooring"
    },
    {
      id: 5,
      name: "Lisa Martinez",
      role: "Restaurant Owner",
      location: "Food District",
      rating: 5,
      text: `Emergency cleaning service saved our restaurant! When we had a kitchen incident, Clean360 responded within hours and had us back to health code compliance the same day. Incredible service!`,
      image: "https://images.pixabay.com/photo/2017/05/30/03/58/woman-2355191_1280.jpg?auto=compress&cs=tinysrgb&w=300",
      service: "Emergency Service"
    },
    {
      id: 6,
      name: "Robert Kim",
      role: "Facility Manager",
      location: "Corporate Center",
      rating: 5,
      text: `Post-construction cleaning was flawless. Clean360 handled the debris, dust, and final detailing perfectly. The building was move-in ready ahead of schedule. Professional team with excellent results.`,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=300&q=80",
      service: "Post-Construction"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, testimonials?.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? "text-warning fill-current" : "text-gray-300"}
      />
    ));
  };

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            What Our Customers Say
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Don't just take our word for it. Here's what our satisfied customers have to say 
            about our cleaning and flooring services.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-xl">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials?.map((testimonial) => (
                <div key={testimonial?.id} className="w-full flex-shrink-0">
                  <div className="bg-card rounded-xl p-8 lg:p-12 shadow-elevation-2">
                    <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
                      {/* Customer Photo */}
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full overflow-hidden">
                          <Image
                            src={testimonial?.image}
                            alt={testimonial?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      {/* Testimonial Content */}
                      <div className="flex-1 text-center lg:text-left">
                        {/* Stars */}
                        <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                          {renderStars(testimonial?.rating)}
                        </div>

                        {/* Quote */}
                        <blockquote className="text-lg lg:text-xl text-text-primary mb-6 leading-relaxed">
                          "{testimonial?.text}"
                        </blockquote>

                        {/* Customer Info */}
                        <div className="space-y-2">
                          <div className="font-semibold text-text-primary text-lg">
                            {testimonial?.name}
                          </div>
                          <div className="text-text-secondary">
                            {testimonial?.role} • {testimonial?.location}
                          </div>
                          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                            <Icon name="CheckCircle" size={14} />
                            <span>{testimonial?.service}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-elevation-2 flex items-center justify-center text-text-primary hover:bg-primary hover:text-white transition-all duration-200"
            aria-label="Previous testimonial"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-elevation-2 flex items-center justify-center text-text-primary hover:bg-primary hover:text-white transition-all duration-200"
            aria-label="Next testimonial"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center space-x-2 mt-8">
          {testimonials?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-primary' : 'bg-border'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-16 pt-16 border-t border-border">
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">500+</div>
            <div className="text-text-secondary">Happy Customers</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">4.9/5</div>
            <div className="text-text-secondary">Average Rating</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">1000+</div>
            <div className="text-text-secondary">Projects Completed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">24/7</div>
            <div className="text-text-secondary">Emergency Support</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;